import { FaqSection } from './faqTypes';

// Who counts as a user, how many there can be, and what a subscription actually
// buys. Grounded in the per-user platform fee and the retention tiers.

export const USERS_ACCESS_SECTION: FaqSection = {
  id: 'users-access',
  title: 'Users and access',
  intro:
    'The platform fee charges per named user per month, so the definition of a user is a pricing question before it is a technical one.',
  items: [
    {
      id: 'what-is-a-user',
      question: 'What are we defining as a user? Person, inspector, company, or enterprise?',
      status: 'proposed',
      answer: [
        '**A user is one named person with a login.** Not a company, not a device, and not a job title shared by a crew.',
        '**The seat is the billable unit.** The platform fee already charges per user per month, and that only works if a seat maps to a person you can name.',
        'Four things get confused in this conversation, so keep them separate:',
      ],
      bullets: [
        '**Account.** The contracting entity, such as the operator, EPC, or contractor. One project has one account. It holds the users, it is not billed as one.',
        '**User (seat).** A named person with a login who can view, query, or approve. This is what is billed.',
        '**Field operator.** Someone running a scanner, welding system, or capture app. Whether they consume a seat depends on whether the capture app needs its own login.',
        '**Data subject.** A pipe, joint, weld, inspection, or coating record. Never a user.',
      ],
      note: '**Open point:** whether field capture devices consume a seat, or whether device access is covered by the hardware rental. On a large spread this changes the seat count materially, so it needs a decision before it is quoted.',
    },
    {
      id: 'user-limits',
      question: 'Are there limits for how many users can be integrated on a platform?',
      status: 'decided',
      answer: [
        '**No technical cap.** The limit is commercial. Price scales with seats, so every extra person is both extra revenue and extra support load.',
        '**The quoted seat count is the contracted seat count.** The pricing sheet already states that platform fees are based on the number of users and modules specified in the proposal.',
        'Two rules to apply when quoting:',
      ],
      bullets: [
        '**Quote a seat band, not an exact number.** For example 25 seats included, then charge the same per-user rate above it. A band absorbs people joining and leaving a project without a re-quote every time.',
        '**Never offer unlimited users.** It removes a real part of the recurring revenue while the support load keeps growing with headcount. Support is already the largest driver in the platform cost build-up, so unlimited costs you twice.',
      ],
    },
    {
      id: 'subscription-saves-data',
      question: 'Does a subscription automatically ensure the ability to save data?',
      status: 'open',
      answer: [
        '**During the project, yes.** Storing what is captured is part of what the platform fee buys. There is no separate charge to save data while the subscription is live.',
        '**After handover, no.** Continued storage is a separate retention line priced per year. The split is deliberate: the subscription pays for the platform while the project runs, and retention pays for keeping the record afterwards.',
        'Two limits worth stating plainly rather than letting a customer discover them:',
      ],
      bullets: [
        '**No connectivity means no cloud copy.** Data stays on the device until it can sync. If connectivity never arrives, nothing reaches the cloud. This is already written into the welding assumptions.',
        '**12 months is included today.** The current pricing sheet covers 12 months after project completion, with extended retention chargeable beyond that.',
      ],
      note: '**To reconcile:** the 12-month included period in the pricing sheet assumptions and the priced per-year retention line describe the same thing two different ways. Align them before this FAQ goes to a customer.',
    },
    {
      id: 'download-vs-login',
      question: 'What is the difference between downloading information and logging into the platform?',
      status: 'proposed',
      answer: [
        '**They are two different products and should be priced differently.**',
        '**Logging in is access.** The application runs, and the customer can query, filter, generate reports, and pull a specific record on demand. During a project that is the platform fee. After handover it is the warm access retention tier.',
        '**Downloading is possession.** The customer takes a copy of their record and holds it themselves. Cold archive sits between the two: CRC Evans keeps an immutable copy and retrieves it on request, but there is no live login.',
      ],
      note: '**Recommendation: never charge for the customer\'s own data.** Give them export at handover as a matter of course, and charge for access, processing, and custody instead. Charging for the data itself invites the argument that CRC Evans is holding a compliance record hostage, and that argument cannot be won.',
    },
  ],
};
